import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { AxiosError } from 'axios';
import { ValidationError } from 'yup';
import { BaseError } from '@utils/types/shared/base.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { PATHS } from '../../../router/paths.ts';
import { useTranslation } from 'react-i18next';
import {
  LoginRequest,
  LoginResponse
} from '@utils/types/profile/user-profile.ts';
import toast from 'react-hot-toast';

export const login = async (payload: LoginRequest): Promise<LoginResponse> => {
  const body = new URLSearchParams();
  body.append('username', payload.username);
  body.append('password', payload.password);
  body.append('remember_me', payload.rememberMe ? 'true' : 'false');

  const { data } = await Axios.post<LoginResponse>(
    POST_URL().login + `?login_type=token`,
    body,
    { withCredentials: true }
  );
  return data;
};

// Robust server-message extractor (FastAPI-friendly)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractServerMessage(err: AxiosError<any>) {
  const data = err.response?.data;

  if (typeof data?.error_description === 'string')
    return data.error_description;
  if (typeof data?.detail === 'string') return data.detail;

  if (Array.isArray(data?.detail)) {
    const msgs = data.detail
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((d: any) => (typeof d?.msg === 'string' ? d.msg : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join('\n');
  }

  return undefined;
}

export function useLogin() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [uiError, setUiError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);

  const mutation = useMutation<
    LoginResponse,
    AxiosError<BaseError>,
    LoginRequest
  >(credentials => login(credentials), {
    useErrorBoundary: false,
    onSuccess: async (data, variables) => {
      try {
        if (data.mfa_required) {
          setMfaRequired(true);
          setUiError(null);
        } else {
          // Store remember_me preference as UI-only state: used solely to
          // pre-check the checkbox on the next visit. Do NOT use this to
          // decide cookie lifetime, session validity, or when to logout —
          // the JWT's own exp claim is the source of truth for auth lifetime.
          if (variables.rememberMe) {
            localStorage.setItem('scriptix_remember_me', 'true');
          } else {
            localStorage.removeItem('scriptix_remember_me');
          }
          TokenExtension.set(data.access_token || data.session_id || '');
          await queryClient.invalidateQueries(QueryKeys.login());
          navigate(PATHS().HOME);
        }
      } catch (error) {
        // Handle any unexpected errors in success handler
        const msg = t('an-error-occurred');
        setUiError(msg);
        toast.error(msg);
      }
    },
    onError: (error: unknown) => {
      try {
        // Handle ValidationError (thrown by axios interceptor for 422 errors)
        if (error instanceof ValidationError) {
          const msg = error.message || t('enter-a-valid-email');
          setUiError(msg);
          toast.error(msg);
          return;
        }

        // Handle AxiosError
        if (error instanceof Error && 'isAxiosError' in error) {
          const axiosError = error as AxiosError<BaseError>;
          const status = axiosError.response?.status;
          const serverMsg = extractServerMessage(axiosError);

          // If interceptor nuked response on auth route, still show a clean message
          const isAuthLogin = /\/auth\/login($|\?)/.test(
            axiosError.config?.url || ''
          );
          if (
            isAuthLogin &&
            (!axiosError.response || axiosError.code === 'ERR_NETWORK')
          ) {
            const msg = t('your-credentials-are-incorrect');
            setUiError(msg);
            toast.error(msg);
            return;
          }

          // Credential / input errors we always show to user
          if ([400, 401, 403, 422].includes(status ?? 0)) {
            // For 401 errors, prioritize server message as it may contain important info
            // like password expiration, account locked, etc.
            let msg = serverMsg;

            if (!msg) {
              msg =
                status === 422
                  ? t('enter-a-valid-email')
                  : t('your-credentials-are-incorrect');
            }

            setUiError(msg);
            toast.error(msg);
            return;
          }

          // True network problems
          if (!axiosError.response) {
            const msg = t(
              'network-error-please-check-your-internet-connection'
            );
            setUiError(msg);
            toast.error(msg);
            return;
          }
        }

        // Fallback for any other error type
        const msg = t('an-error-occurred');
        setUiError(msg);
        toast.error(msg);
      } catch (handlerError) {
        // Catch any errors in the error handler itself
        const msg = t('an-error-occurred');
        setUiError(msg);
        toast.error(msg);
      }
    }
  });

  return { ...mutation, uiError, mfaRequired };
}
